<script lang="ts">
import { computed, defineComponent, onMounted, reactive, toRefs } from 'vue'
import isEmpty from 'lodash/isEmpty'
import LenCategoryRender from './child/LenCategoryChild.vue'
import CardHeader from '@/components/ui/card/CardHeader.vue'
import CardItem from '@/components/ui/card/CardItem.vue'
import { useCustomization } from '@/hooks/useCustomization'
import { useEnhancer } from '@/app/enhancer'
import { lenTypes } from '@/hooks/enums'
import { EventType, useEvents } from '@/hooks/useEvents'

export default defineComponent({
  name: 'GoLens',
  components: { CardHeader, CardItem, LenCategoryRender },
  emits: ['ok', 'back'],
  setup(_, { emit }) {
    const customization = useCustomization()
    const { storeFavorites } = useEnhancer()
    const eventBus = useEvents()

    const state = reactive({
      mode: '',
      filtered: 'normal',
      selectedGroupKey: '',
      typeOptions: [
        { label: 'ノーマルカラー', value: 'normal' },
        { label: 'グラデーション', value: 'gradient' },
      ],
      functionStep: 1,
    })

    const handleBack = () => {
      state.mode = ''
      state.functionStep = 1
    }

    const onSelectFilter = (key: string) => {
      state.selectedGroupKey = key
    }

    const nextIntro = (key: string) => {
      state.functionStep = 0
      onSelectFilter(key)
    }

    const filteredOptions = computed<AnyObject[]>(() => {
      return customization.getLensFiltered.value.filter((item) => {
        if (state.mode === lenTypes.ORDINARY)
          return item.type.includes([state.mode, state.filtered].join('_'))

        // ? Render condition for lens function
        const condition
          = item?.filtered?.includes(customization.getCurrentModel.value)
          && item.type.includes(state.mode)

        return condition
      })
    })

    const firstFilteredOption = computed(() => {
      return filteredOptions.value?.[0]?.key
    })

    const lensColors = computed<LensColorType[]>(() => {
      return (
        customization.getLensGrouping.value?.[state.selectedGroupKey] ?? []
      )
    })

    const selectedGroupName = computed(() => {
      if (!state.selectedGroupKey)
        return ''
      const [firstName, secondName] = state.selectedGroupKey.split('  ')
      // console.info({
      //   selectedGroupKey: state.selectedGroupKey,
      //   firstName,
      //   secondName,
      // })
      return secondName || firstName
    })

    const firstLenColor = computed(() => {
      return lensColors.value?.[0]
    })

    const currentActive = computed(() => {
      const key = storeFavorites.selectedLen?.value
      if (key === 'clear')
        return lenTypes.CLEAR
      if (key.includes('function'))
        return lenTypes.FUNCTION
      return lenTypes.ORDINARY
    })

    const activeMode = (item) => {
      return item.key === currentActive.value
    }

    const computeStyle = (item: LensColorType) => {
      const { renderColor } = item
      return {
        ...(renderColor && { background: renderColor }),
        // ...(photo && {
        //   backgroundImage: `url(${photo})`,
        //   backgroundSize: 'cover',
        //   backgroundRepeat: 'no-repeat',
        //   backgroundPosition: '50% 50%',
        // }),
      }
    }

    const isCurrent = item =>
      item.value === storeFavorites.selectedLen?.value

    const handleClose = () => {
      emit('back')
    }

    const selected = (item) => {
      if (isCurrent(item))
        return
      storeFavorites.setCurrentConfig('len', item)
      // TODO: Emit event to update constraints the lens option
      eventBus.emit(EventType.LENS_SELECTED)
    }

    const resetDefault = (initial = false) => {
      state.selectedGroupKey = firstFilteredOption.value
      if (initial && isEmpty(storeFavorites.selectedLen))
        selected(firstLenColor.value)
    }

    const setMode = (type) => {
      if (type === lenTypes.CLEAR) {
        selected(customization.getLensClear.value[0])
        handleClose()
      }
      else {
        state.mode = type
        resetDefault(true)
      }
    }

    const filteredLens = (key: string) => {
      state.filtered = key
      resetDefault()
    }

    onMounted(() => {
      const len = storeFavorites.selectedLen
      if (isEmpty(len))
        return

      const [, lenFilter] = len?.filtered?.split('_')
      // state.mode = lenMode;
      state.filtered = lenFilter
      onSelectFilter(len.description)
    })

    return {
      ...toRefs(state),
      isCurrent,
      activeMode,
      lensOptions: customization.getLensTypes,
      filteredOptions,
      lenTypes,
      lensColors,
      selectedGroupName,
      handleBack,
      setMode,
      onSelectFilter,
      selected,
      computeStyle,
      resetDefault,
      nextIntro,
      filteredLens,
      handleClose,
    }
  },
})
</script>

<template>
  <div v-if="mode === lenTypes.ORDINARY" class="animate_500">
    <CardHeader @back="handleBack" @close="handleClose">
      <template #title>
        <div class="row full-width q-pb-xs items-center">
          <div class="col-4">
            <p class="text-black text-weight-medium text-body1 q-mb-none">
              色で選ぶ
            </p>
          </div>
          <div class="col-8">
            <div
              class="
                row
                q-btn-group--flat
                bg-white
                q-btn-group--spread
                q-btn--rounded
                q-mt-xs
              "
            >
              <q-btn
                v-for="btn in typeOptions"
                :key="btn.value"
                rounded
                :flat="filtered !== btn.value"
                dense
                spread
                no-caps
                color="primary"
                padding="4px 8px"
                :text-color="filtered !== btn.value ? 'black' : 'white'"
                size="sm"
                @click="() => filteredLens(btn.value)"
              >
                {{ btn.label }}
              </q-btn>
            </div>
          </div>
        </div>
      </template>
    </CardHeader>
    <q-virtual-scroll
      v-slot="{ item }"
      :items="filteredOptions"
      virtual-scroll-horizontal
    >
      <q-btn
        :key="item.key"
        :style="computeStyle(item)"
        round
        dense
        size="sm"
        no-wrap
        class="q-mx-md q-my-md" :class="[
          { zoom_large: selectedGroupKey === item.key },
        ]"
        @click="onSelectFilter(item.key)"
      />
    </q-virtual-scroll>
    <p class="text-body2 text-center text-weight-medium q-my-sm">
      {{ selectedGroupName }}
    </p>
    <q-separator class="q-mb-sm" />
    <q-virtual-scroll
      v-slot="{ item }"
      :items="lensColors"
      virtual-scroll-horizontal
      class="q-px-sm q-pb-sm"
    >
      <div :key="item.value" class="q-pa-xs">
        <CardItem
          :item="item"
          :is-active="isCurrent(item)"
          @selected="selected(item)"
        />
      </div>
    </q-virtual-scroll>
  </div>
  <div v-else-if="mode === lenTypes.FUNCTION" class="animate_500">
    <template v-if="functionStep === 1">
      <CardHeader
        title="機能で選ぶ"
        description="機能レンズ一覧"
        @back="handleBack"
        @close="handleClose"
      />
      <div class="row q-col-gutter-sm q-px-md q-py-sm">
        <div
          v-for="item in filteredOptions"
          :key="item.key"
          class="col-6 col-sm-4"
        >
          <q-img
            v-ripple
            fit="cover"
            :src="item.photo"
            :alt="item.subTitle"
            class="full-width cursor-pointer"
            style="height: 120px"
            no-spinner
            no-transition
            fetchpriority="high"
            @click="nextIntro(item.key)"
          >
            <div
              style="padding: 4px"
              class="absolute-center full-width bg-transparent"
            >
              <div
                class="
                  bg-blur
                  text-black text-center
                  rounded-borders
                  q-py-sm
                  shadow-1
                "
              >
                <q-item-label class="text-small text-weight-medium" :lines="1">
                  {{ item.key }} {{ item.extraTitle || '' }}
                </q-item-label>
                <q-separator class="q-mx-sm q-my-xs bg-black" />
                <q-item-label
                  class="text-small text-weight-light text-dark"
                  caption
                >
                  {{ item.subTitle }}
                </q-item-label>
              </div>
            </div>
          </q-img>
        </div>
      </div>
      <div class="text-caption text-center q-pb-sm full-width">
        屈指率次第で選べないレンズがあります
      </div>
    </template>
    <template v-if="functionStep === 0">
      <CardHeader
        title="機能で選ぶ"
        description="機能レンズ 一覧"
        @back="handleBack"
        @close="handleClose"
      />
      <q-virtual-scroll
        v-slot="{ item, index }"
        :items="filteredOptions"
        virtual-scroll-horizontal
        class="q-px-sm q-pb-xs"
      >
        <q-btn
          :key="index"
          color="primary"
          :text-color="selectedGroupKey === item.key ? 'white' : 'black'"
          :outline="selectedGroupKey !== item.key"
          rounded
          dense
          size="sm"
          no-wrap
          class="q-px-md q-mx-xs q-py-xs q-my-xs"
          @click="onSelectFilter(item.key)"
        >
          {{ item.key }}
        </q-btn>
      </q-virtual-scroll>
      <q-virtual-scroll
        v-slot="{ item }"
        :items="lensColors"
        virtual-scroll-horizontal
        class="q-px-md q-pb-sm"
      >
        <CardItem
          :key="item.value"
          :item="item"
          :is-active="isCurrent(item)"
          @selected="selected(item)"
        />
      </q-virtual-scroll>
    </template>
  </div>
  <div v-else class="animate_500">
    <CardHeader
      title="LENS"
      description="レンズ"
      hidden-left
      @close="handleClose"
    />
    <div class="row full-height items-center q-col-gutter-sm q-px-md q-py-sm">
      <LenCategoryRender
        v-for="item in lensOptions"
        :key="item.key"
        :item="item"
        :active-mode="activeMode(item)"
        @set-mode="setMode"
      />
      <div class="text-caption text-center q-py-sm full-width">
        ※屈折率次第で選べないレンズがあります
      </div>
    </div>
  </div>
</template>
